/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Share2, Link as LinkIcon, Copy, Trash2, ArrowLeft, Eye, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareLinksPage = () => {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    department_filter: 'NONE',
    expires_in_days: '30'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/share-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(response.data);
    } catch (error) {
      toast.error('Failed to load share links');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        department_filter: formData.department_filter === 'NONE' ? null : formData.department_filter,
        expires_in_days: parseInt(formData.expires_in_days)
      };
      await axios.post(`${API}/projects/${projectId}/share-links`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Share link created!');
      setShowCreateDialog(false);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (linkToken) => {
    const url = `${window.location.origin}/share/${linkToken}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const handleDeleteLink = async (linkToken) => {
    if (!window.confirm('Delete this share link?')) return;
    try {
      await axios.delete(`${API}/share-links/${linkToken}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Share link deleted');
      fetchLinks();
    } catch (error) {
      toast.error('Failed to delete share link');
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-8" data-testid="share-links-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(`/projects/${projectId}/breakdown`)}
              className="text-[#a0a0a0] hover:text-[#f0f0f0] mb-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Breakdown
            </Button>
            <h1 className="font-['DM_Serif_Display'] text-4xl text-[#f0f0f0] mb-2">Share Links</h1>
            <p className="text-[#a0a0a0]">Create view-only links to share with anyone</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
            data-testid="create-link-btn"
          >
            <Share2 className="h-5 w-5 mr-2" />
            Create Link
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#a0a0a0]">Loading links...</div>
        ) : links.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center" data-testid="no-links">
            <LinkIcon className="h-16 w-16 text-[#a0a0a0] mx-auto mb-4" />
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">No share links yet</h3>
            <p className="text-[#a0a0a0]">Create one to share breakdown views with anyone</p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="links-list">
            {links.map((link) => (
              <Card key={link.token} className="bg-[#1a1a1a] border-[#2a2a2a] p-6" data-testid={`link-${link.token}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <LinkIcon className="h-5 w-5 text-[#e8c547] flex-shrink-0" />
                      <code className="text-[#f0f0f0] text-sm font-mono truncate">{`${window.location.origin}/share/${link.token}`}</code>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {link.department_filter ? (
                        <Badge className="bg-[#4a9eff]/20 text-[#4a9eff]">{link.department_filter}</Badge>
                      ) : (
                        <Badge className="bg-[#e8c547]/20 text-[#e8c547]">Full Breakdown</Badge>
                      )}
                      <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0]">
                        <Eye className="h-3 w-3 mr-1" />
                        {link.view_count || 0} views
                      </Badge>
                      {link.expires_at && (
                        <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0]">
                          <Clock className="h-3 w-3 mr-1" />
                          Expires {new Date(link.expires_at).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(link.token)}
                      className="border-[#2a2a2a] text-[#f0f0f0]"
                      data-testid={`copy-link-${link.token}`}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLink(link.token)}
                      className="text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-link-${link.token}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
            <DialogHeader>
              <DialogTitle className="font-['DM_Serif_Display'] text-2xl">Create Share Link</DialogTitle>
              <DialogDescription className="text-[#a0a0a0]">
                Generate a view-only link that anyone can access without logging in.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Filter by Department (optional)</label>
                <Select value={formData.department_filter} onValueChange={(v) => setFormData({...formData, department_filter: v})}>
                  <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="dept-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                    <SelectItem value="NONE">Full Breakdown (All)</SelectItem>
                    <SelectItem value="ART">Art</SelectItem>
                    <SelectItem value="COSTUME_WARDROBE">Costume/Wardrobe</SelectItem>
                    <SelectItem value="CASTING">Casting</SelectItem>
                    <SelectItem value="MAKEUP_HAIR">Makeup/Hair</SelectItem>
                    <SelectItem value="VFX">VFX</SelectItem>
                    <SelectItem value="LOCATIONS">Locations</SelectItem>
                    <SelectItem value="SFX_STUNTS">SFX/Stunts</SelectItem>
                    <SelectItem value="SOUND">Sound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Expires In (days)</label>
                <Select value={formData.expires_in_days} onValueChange={(v) => setFormData({...formData, expires_in_days: v})}>
                  <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="expires-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={creating}
                className="w-full bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
                data-testid="submit-create-link-btn"
              >
                {creating ? 'Creating...' : 'Create Share Link'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ShareLinksPage;
